import store from '@/store'
import { convertKrwToUsdt, formatCurrencyAmount, formatUsdtAmount, formatWalletProcDate } from '@/utils/format'
import { extractTetherAddress } from '@/utils/qrCode'
import i18n from '@/utils/i18n'
import { resolveSiteCurrency } from '@/utils/siteBrand'

function siteCurrency () {
  return resolveSiteCurrency(store.state.siteInfo)
}

function formatSiteCurrency (value) {
  return formatCurrencyAmount(value, siteCurrency())
}

export const CHARGE_LIST_PAGE_SIZE = 10
export const WITHDRAW_LIST_PAGE_SIZE = 10
export const MIN_WITHDRAW_AMOUNT = 10000

const WITHDRAW_REQUEST_TOOMANY_CODE = 2026
const WITHDRAW_MONEY_MIN_CODE = 2027
const BALANCE_NOT_ENOUGH_CODE = 2006
const WITHDRAWER_LIMIT_ERROR_CODE = 2043

export function resolveWithdrawRequestError (body) {
  if (!body) return i18n.t('wallet.withdrawFail')
  const code = Number(body.code)
  const message = String(body.message || '').toUpperCase()
  if (code === WITHDRAW_REQUEST_TOOMANY_CODE || message === 'CHARGE_REQUEST_TOOMANY') {
    return i18n.t('wallet.withdrawPending')
  }
  if (code === WITHDRAW_MONEY_MIN_CODE || message === 'WITHDRAW_MONEY_MIN') {
    return i18n.t('wallet.withdrawMinAmount', {
      amount: formatSiteCurrency(MIN_WITHDRAW_AMOUNT)
    })
  }
  if (code === BALANCE_NOT_ENOUGH_CODE || message === 'BALANCE_NOT_ENOUGH') {
    return i18n.t('wallet.withdrawExceedBalance')
  }
  if (code === WITHDRAWER_LIMIT_ERROR_CODE || message === 'WITHDRAWER_LIMIT_ERROR') {
    const minutes = body.message ? String(body.message).trim() : ''
    return minutes
      ? i18n.t('wallet.withdrawLimitMinutes', { minutes })
      : i18n.t('wallet.withdrawLimitGeneric')
  }
  if (body.message) return body.message
  return i18n.t('wallet.withdrawFail')
}

export function normalizeChargeList (payload, { completedOnly = false } = {}) {
  if (!payload) return []
  const list = Array.isArray(payload.list) ? payload.list : []
  return list.filter(item => {
    if (!item || item.idx == null) return false
    if (completedOnly && Number(item.status) !== 4) return false
    return true
  })
}

export function chargeStatusLabel (status) {
  const value = Number(status)
  if (value === 1) return i18n.t('wallet.statusRequest')
  if (value === 2) return i18n.t('wallet.statusWaiting')
  if (value === 4) return i18n.t('wallet.statusComplete')
  if (value === 8) return i18n.t('wallet.statusCancel')
  if (value === 16) return i18n.t('wallet.statusDelete')
  return i18n.t('wallet.statusUnknown')
}

export function chargeStatusClass (status) {
  const value = Number(status)
  if (value === 1) return 'wallet-status--request'
  if (value === 2) return 'wallet-status--waiting'
  if (value === 4) return 'wallet-status--complete'
  if (value === 8) return 'wallet-status--cancel'
  return 'wallet-status--unknown'
}

/** 입금 구분: 1=일반 충전요청, 2=강제충전 */
export function chargeTypeLabel (type) {
  const value = Number(type)
  if (value === 1) return i18n.t('wallet.chargeTypeNormal')
  if (value === 2) return i18n.t('wallet.chargeTypeForce')
  return i18n.t('wallet.chargeTypeOther')
}

export function chargeTypeClass (type) {
  const value = Number(type)
  if (value === 1) return 'wallet-type--normal'
  if (value === 2) return 'wallet-type--force'
  return 'wallet-type--unknown'
}

/** 신청·대기 등 미처리 상태 */
export function isChargePending (status) {
  const value = Number(status)
  return value === 1 || value === 2
}

export function formatChargeProcDate (row) {
  return formatWalletProcDate(row && row.procdate, {
    pending: row ? isChargePending(row.status) : true
  })
}

const TETHER_CHARGE_ACCOUNT_PREFIX = '테더:'

/** 테더 입금 요청 account: 테더:{TRC20주소} */
export function buildTetherChargeAccount (tetherAddress) {
  const address = String(tetherAddress || '').trim()
  if (!address) return ''
  if (address.startsWith(TETHER_CHARGE_ACCOUNT_PREFIX)) return address
  return `${TETHER_CHARGE_ACCOUNT_PREFIX}${address}`
}

/** 계좌 입금 요청 account: 입금자명:… 은행명:… 계좌정보:… */
export function buildBankChargeAccount ({ bankMaster, bankName, bankAccount }) {
  const master = String(bankMaster || '').trim()
  const name = String(bankName || '').trim()
  const account = String(bankAccount || '').trim()
  if (!master || !name || !account) return ''

  return `입금자명:${master} 은행명:${name} 계좌정보:${account}`
}

/** 문자열·객체에서 { ... } JSON 블록 추출 */
export function extractJsonObjectFromText (value) {
  if (value == null || value === '') return null
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value
  }

  const str = String(value).trim()
  if (!str) return null

  if (str.charAt(0) === '{') {
    try {
      const parsed = JSON.parse(str)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch (err) {
      // fall through
    }
  }

  const start = str.indexOf('{')
  const end = str.lastIndexOf('}')
  if (start >= 0 && end > start) {
    try {
      const parsed = JSON.parse(str.slice(start, end + 1))
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch (err) {
      return null
    }
  }

  return null
}

/** charge_req 응답 bank_name — JObject 또는 문자열 내 JSON */
export function parseChargeBankNameField (bankNameRaw) {
  return extractJsonObjectFromText(bankNameRaw)
}

function pickTetherAddressFromBankNameObject (obj) {
  if (!obj) return ''
  const tether = obj.Tether || obj.tether || obj.TETHER || ''
  return String(tether).trim()
}

function pickTetherAddressFromText (text) {
  const obj = extractJsonObjectFromText(text)
  const fromObj = pickTetherAddressFromBankNameObject(obj)
  if (fromObj) return fromObj

  const raw = String(text || '')
  const match = raw.match(/"Tether"\s*:\s*"([^"]+)"/i)
  if (match && match[1]) return String(match[1]).trim()

  const legacy = raw.replace(/^테더주소\s*안내\s*:\s*/i, '').trim()
  if (legacy && legacy.indexOf('{') < 0 && legacy.indexOf('"Tether"') < 0) {
    return legacy
  }

  return ''
}

/** charge_req 응답 — 테더 입금: Tether 주소만 */
export function extractTetherFromChargeData (data) {
  if (!data) return ''

  const fromBankName = pickTetherAddressFromText(data.bank_name)
  if (fromBankName) return fromBankName

  if (data.bankinfo) {
    const fromBankinfo = pickTetherAddressFromText(data.bankinfo)
    if (fromBankinfo) return fromBankinfo

    const bankinfoText = String(data.bankinfo || '')
    if (bankinfoText.indexOf('{') < 0) {
      return extractTetherAddress(data.bankinfo)
    }
  }

  return ''
}

function pickBankNameFromText (text) {
  const obj = extractJsonObjectFromText(text)
  if (!obj) return ''
  return String(obj.bank_name || obj.bankName || '').trim()
}

/** "예금주 계좌번호 { ... }" — JSON 앞 공백 구분: 1=예금주, 2=계좌번호 */
function parseBankPrefixFromText (text) {
  const str = String(text || '').trim()
  if (!str) {
    return { bankMaster: '', bankAccount: '' }
  }

  const brace = str.indexOf('{')
  const prefix = brace >= 0 ? str.slice(0, brace).trim() : str
  if (!prefix) {
    return { bankMaster: '', bankAccount: '' }
  }

  const parts = prefix.split(/\s+/).filter(function (part) {
    return !!part
  })

  return {
    bankMaster: parts[0] ? String(parts[0]).trim() : '',
    bankAccount: parts[1] ? String(parts[1]).trim() : ''
  }
}

/** charge_req 응답 — 계좌 입금: bank_name(JSON), bank_account·bank_master(앞쪽 텍스트) */
export function extractBankFromChargeData (data) {
  if (!data) {
    return { bankName: '', bankAccount: '', bankMaster: '' }
  }

  const sourceText = data.bank_name || data.bankinfo || ''
  const obj = parseChargeBankNameField(sourceText)
  const prefix = parseBankPrefixFromText(sourceText)

  let bankName = pickBankNameFromText(sourceText)
  if (!bankName && obj) {
    bankName = String(obj.bank_name || obj.bankName || '').trim()
  }

  const bankAccount = String(
    data.bank_account ||
    prefix.bankAccount ||
    (obj && (obj.bank_account || obj.bankAccount)) ||
    ''
  ).trim()

  const bankMaster = String(
    data.bank_master ||
    prefix.bankMaster ||
    (obj && (obj.bank_master || obj.bankMaster)) ||
    ''
  ).trim()

  return {
    bankName: bankName,
    bankAccount: bankAccount,
    bankMaster: bankMaster
  }
}

export function formatBankDepositCopyText ({ bankName, bankAccount, bankMaster } = {}) {
  const lines = []
  if (bankName) lines.push(`${i18n.t('wallet.bankName')}: ${bankName}`)
  if (bankAccount) lines.push(`${i18n.t('wallet.accountNumber')}: ${bankAccount}`)
  if (bankMaster) lines.push(`${i18n.t('wallet.accountHolder')}: ${bankMaster}`)
  return lines.join('\n')
}

/** 입금 요청 memo: 입금액 50,000원 = 35.12USDT입금요청 (빗썸 USDT 환율 기준) */
export function buildChargeRequestMemo (krwAmount, ratePerUsdt) {
  const krw = formatSiteCurrency(krwAmount)
  const usdt = convertKrwToUsdt(krwAmount, ratePerUsdt)
  if (usdt == null) return ''
  const usdtStr = formatUsdtAmount(usdt, { maxDecimals: 2, minDecimals: 2 })
  return i18n.t('wallet.chargeMemo', { krw, usdt: usdtStr })
}

/** 출금 요청 memo: 출금액 50,000원 = 35.12USDT출금요청 (빗썸 USDT 환율 기준) */
export function buildWithdrawRequestMemo (krwAmount, ratePerUsdt) {
  const krw = formatSiteCurrency(krwAmount)
  const usdt = convertKrwToUsdt(krwAmount, ratePerUsdt)
  if (usdt == null) return ''
  const usdtStr = formatUsdtAmount(usdt, { maxDecimals: 2, minDecimals: 2 })
  return i18n.t('wallet.withdrawMemo', { krw, usdt: usdtStr })
}

export function normalizeWithdrawList (payload, { completedOnly = false } = {}) {
  if (!payload) return []
  const list = Array.isArray(payload.list) ? payload.list : []
  return list.filter(item => {
    if (!item || item.idx == null) return false
    if (completedOnly && Number(item.status) !== 4) return false
    return true
  })
}

export const withdrawStatusLabel = chargeStatusLabel
export const withdrawStatusClass = chargeStatusClass

/** 출금 구분: 1=일반출금, 2=강제출금 */
export function withdrawTypeLabel (type) {
  const value = Number(type)
  if (value === 1) return i18n.t('wallet.withdrawTypeNormal')
  if (value === 2) return i18n.t('wallet.withdrawTypeForce')
  return i18n.t('wallet.withdrawTypeOther')
}

export function withdrawTypeClass (type) {
  const value = Number(type)
  if (value === 1) return 'wallet-type--normal'
  if (value === 2) return 'wallet-type--force'
  return 'wallet-type--unknown'
}

export function isWithdrawPending (status) {
  return isChargePending(status)
}

export function formatWithdrawProcDate (row) {
  return formatWalletProcDate(row && row.procdate, {
    pending: row ? isWithdrawPending(row.status) : true
  })
}

export function computeTotalBet (sbet, cbet) {
  return (Number(sbet) || 0) + (Number(cbet) || 0)
}

/** 총입금 × 출금베팅비율(%) */
export function computeWithdrawBetThreshold (totalCharge, withdrawLimitPercent) {
  const charge = Number(totalCharge) || 0
  const percent = Number(withdrawLimitPercent) || 0
  return charge * percent / 100
}

/** 총베팅액이 (총입금 × 비율)보다 큰 경우 */
export function isWithdrawBetRequirementMet (totalCharge, totalBet, withdrawLimitPercent) {
  return totalBet > computeWithdrawBetThreshold(totalCharge, withdrawLimitPercent)
}

export function computeWithdrawableBalance ({
  balance,
  bonus,
  totalCharge,
  totalBet,
  withdrawLimitPercent
}) {
  if (
    balance == null ||
    bonus == null ||
    totalCharge == null ||
    totalBet == null ||
    withdrawLimitPercent == null
  ) {
    return null
  }
  const money = Number(balance) || 0
  const bonusAmount = Number(bonus) || 0
  if (isWithdrawBetRequirementMet(totalCharge, totalBet, withdrawLimitPercent)) {
    return Math.max(0, money)
  }
  return Math.max(0, money - bonusAmount)
}
